"use client";

export default function RetailDashboardsSection() {
  return (
    <section className="py-24 bg-cream px-4 sm:px-6 lg:px-8 xl:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-navy text-2xl sm:text-3xl md:text-4xl xl:text-[3rem] leading-[1.2] font-bold mb-4">
            Role-Specific Intelligence for Every <span className="italic">Stakeholder</span>
          </h2>
          <p className="text-charcoal/70 text-lg max-w-2xl mx-auto">
            Purpose-built dashboards for every decision-maker in your retail organisation.
          </p>
        </div>

        {/* Dashboard Cards */}
        <div className="space-y-12">

          {/* Dashboard 1: CEO / CFO */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 1</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">CEO / CFO Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Executive-level visibility into revenue performance, inventory efficiency, and chain-wide financial health.</p>
                  <div className="space-y-2 mb-8">
                    {['Total Revenue & Growth Rate', 'Same-Store Sales Comparison', 'Gross Margin & EBITDA', 'Inventory Efficiency']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
              {/* Right: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px]">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  {/* Dashboard frame */}
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  {/* Top bar */}
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="200" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">CEO / CFO Executive Dashboard</text>

                  {/* KPI Cards Row */}
                  {[
                    { x: 8, label: '₹847Cr', sub: 'Revenue', color: '#C5A572' },
                    { x: 112, label: '7.2×', sub: 'Inv. Turn', color: '#4ade80' },
                    { x: 216, label: '+23%', sub: 'YoY Growth', color: '#60a5fa' },
                    { x: 320, label: '38.4%', sub: 'Gross Margin', color: '#f472b6' },
                  ]?.map((kpi, i) => (
                    <g key={i}>
                      <rect x={kpi?.x} y="40" width="96" height="44" rx="4" fill="#ffffff08" stroke="#ffffff10" strokeWidth="0.5" />
                      <text x={kpi?.x + 48} y="58" textAnchor="middle" fill={kpi?.color} fontSize="13" fontWeight="bold" fontFamily="sans-serif">{kpi?.label}</text>
                      <text x={kpi?.x + 48} y="72" textAnchor="middle" fill="#ffffff60" fontSize="7" fontFamily="sans-serif">{kpi?.sub}</text>
                    </g>
                  ))}

                  {/* Revenue Trend Line Chart */}
                  <rect x="8" y="92" width="240" height="100" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="104" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Revenue Trend (Monthly)</text>
                  {/* Chart axes */}
                  <line x1="20" y1="182" x2="240" y2="182" stroke="#ffffff20" strokeWidth="0.5" />
                  <line x1="20" y1="112" x2="20" y2="182" stroke="#ffffff20" strokeWidth="0.5" />
                  {/* Area fill */}
                  <path d="M 20 165 L 50 158 L 80 152 L 110 145 L 140 138 L 170 128 L 200 120 L 230 112 L 230 182 L 20 182 Z" fill="#C5A572" opacity="0.12" />
                  {/* Line */}
                  <polyline points="20,165 50,158 80,152 110,145 140,138 170,128 200,120 230,112" fill="none" stroke="#C5A572" strokeWidth="1.5" strokeLinejoin="round" />
                  {/* Dots */}
                  {[[20,165],[50,158],[80,152],[110,145],[140,138],[170,128],[200,120],[230,112]]?.map(([x,y],i) => (
                    <circle key={i} cx={x} cy={y} r="2" fill="#C5A572" />
                  ))}
                  {/* Month labels */}
                  {['Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb']?.map((m, i) => (
                    <text key={i} x={20 + i * 30} y="190" fill="#ffffff40" fontSize="6" textAnchor="middle" fontFamily="sans-serif">{m}</text>
                  ))}

                  {/* Top Stores Bar Chart */}
                  <rect x="256" y="92" width="156" height="100" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="264" y="104" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Top Stores by Revenue</text>
                  {[
                    { label: 'Mumbai-01', val: 130, color: '#C5A572' },
                    { label: 'Delhi-03', val: 110, color: '#C5A572' },
                    { label: 'Pune-02', val: 90, color: '#C5A572' },
                    { label: 'Hyd-01', val: 75, color: '#C5A572' },
                    { label: 'Blr-04', val: 60, color: '#C5A572' },
                  ]?.map((bar, i) => (
                    <g key={i}>
                      <text x="264" y={118 + i * 14} fill="#ffffff60" fontSize="6" fontFamily="sans-serif">{bar?.label}</text>
                      <rect x="310" y={110 + i * 14} width={bar?.val * 0.6} height="8" rx="2" fill="#C5A572" opacity={0.3 + i * 0.1} />
                      <text x={312 + bar?.val * 0.6} y={118 + i * 14} fill="#C5A572" fontSize="6" fontFamily="sans-serif">₹{bar?.val}Cr</text>
                    </g>
                  ))}

                  {/* Gross Margin Gauge */}
                  <rect x="8" y="200" width="120" height="72" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="212" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Gross Margin %</text>
                  <path d="M 68 258 m -36 0 a 36 36 0 0 1 72 0" fill="none" stroke="#ffffff15" strokeWidth="8" strokeLinecap="round" />
                  <path d="M 68 258 m -36 0 a 36 36 0 0 1 52 -22" fill="none" stroke="#C5A572" strokeWidth="8" strokeLinecap="round" />
                  <text x="68" y="252" textAnchor="middle" fill="#C5A572" fontSize="12" fontWeight="bold" fontFamily="sans-serif">38.4%</text>
                  <text x="68" y="264" textAnchor="middle" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">Gross Margin</text>

                  {/* Cash Flow Waterfall */}
                  <rect x="136" y="200" width="276" height="72" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="144" y="212" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Cash Flow from Operations (₹Cr)</text>
                  {[
                    { x: 144, h: 30, color: '#4ade80', label: 'Sales' },
                    { x: 186, h: -18, color: '#f87171', label: 'COGS' },
                    { x: 228, h: -8, color: '#f87171', label: 'Ops' },
                    { x: 270, h: -5, color: '#f87171', label: 'Tax' },
                    { x: 312, h: 22, color: '#60a5fa', label: 'Net' },
                    { x: 354, h: 22, color: '#C5A572', label: 'FCF' },
                  ]?.map((bar, i) => {
                    const base = 255;
                    const y = bar?.h > 0 ? base - bar?.h : base;
                    const h = Math.abs(bar?.h);
                    return (
                      <g key={i}>
                        <rect x={bar?.x} y={y} width="28" height={h} rx="1" fill={bar?.color} opacity="0.7" />
                        <text x={bar?.x + 14} y="268" textAnchor="middle" fill="#ffffff50" fontSize="5.5" fontFamily="sans-serif">{bar?.label}</text>
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>

          {/* Dashboard 2: Store Operations */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px] order-2 lg:order-1">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="200" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">Store Operations Dashboard</text>

                  {/* Inventory Heatmap */}
                  <rect x="8" y="40" width="200" height="130" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Store Inventory Heatmap</text>
                  {/* Heatmap grid */}
                  {(() => {
                    const colors = [
                      ['#4ade80','#4ade80','#fbbf24','#4ade80','#4ade80'],
                      ['#4ade80','#f87171','#4ade80','#fbbf24','#4ade80'],
                      ['#fbbf24','#4ade80','#4ade80','#f87171','#4ade80'],
                      ['#4ade80','#4ade80','#fbbf24','#4ade80','#f87171'],
                      ['#4ade80','#fbbf24','#4ade80','#4ade80','#4ade80'],
                    ];
                    const labels = ['S01','S02','S03','S04','S05'];
                    const rows = ['Cat-A','Cat-B','Cat-C','Cat-D','Cat-E'];
                    return (
                      <>
                        {labels?.map((l, i) => (
                          <text key={i} x={38 + i * 32} y="64" textAnchor="middle" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">{l}</text>
                        ))}
                        {rows?.map((r, ri) => (
                          <g key={ri}>
                            <text x="16" y={78 + ri * 18} fill="#ffffff50" fontSize="6" fontFamily="sans-serif">{r}</text>
                            {colors?.[ri]?.map((c, ci) => (
                              <rect key={ci} x={22 + ci * 32} y={68 + ri * 18} width="26" height="14" rx="2" fill={c} opacity="0.7" />
                            ))}
                          </g>
                        ))}
                        {/* Legend */}
                        <circle cx="16" cy="162" r="4" fill="#4ade80" opacity="0.7" />
                        <text x="24" y="165" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">OK</text>
                        <circle cx="44" cy="162" r="4" fill="#fbbf24" opacity="0.7" />
                        <text x="52" y="165" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">Low</text>
                        <circle cx="72" cy="162" r="4" fill="#f87171" opacity="0.7" />
                        <text x="80" y="165" fill="#ffffff50" fontSize="6" fontFamily="sans-serif">Stockout</text>
                      </>
                    );
                  })()}

                  {/* Stockout Alerts */}
                  <rect x="216" y="40" width="196" height="130" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="224" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Stockout Alerts</text>
                  {[
                    { sku: 'SKU-4821', store: 'Delhi-03', status: 'CRITICAL', color: '#f87171' },
                    { sku: 'SKU-2934', store: 'Pune-02', status: 'LOW', color: '#fbbf24' },
                    { sku: 'SKU-7612', store: 'Mumbai-01', status: 'LOW', color: '#fbbf24' },
                    { sku: 'SKU-1093', store: 'Hyd-01', status: 'CRITICAL', color: '#f87171' },
                    { sku: 'SKU-5547', store: 'Blr-04', status: 'LOW', color: '#fbbf24' },
                  ]?.map((alert, i) => (
                    <g key={i}>
                      <rect x="224" y={58 + i * 20} width="180" height="16" rx="2" fill="#ffffff05" />
                      <text x="230" y={69 + i * 20} fill="#ffffff80" fontSize="6.5" fontFamily="sans-serif">{alert?.sku}</text>
                      <text x="290" y={69 + i * 20} fill="#ffffff50" fontSize="6" fontFamily="sans-serif">{alert?.store}</text>
                      <rect x="352" y={60 + i * 20} width="44" height="12" rx="2" fill={alert?.color} opacity="0.2" />
                      <text x="374" y={69 + i * 20} textAnchor="middle" fill={alert?.color} fontSize="6" fontFamily="sans-serif">{alert?.status}</text>
                    </g>
                  ))}

                  {/* Stock Level Progress Bars */}
                  <rect x="8" y="178" width="200" height="94" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="190" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Stock Levels by Category</text>
                  {[
                    { cat: 'Electronics', pct: 82, color: '#4ade80' },
                    { cat: 'Apparel', pct: 45, color: '#fbbf24' },
                    { cat: 'Grocery', pct: 91, color: '#4ade80' },
                    { cat: 'Home & Living', pct: 28, color: '#f87171' },
                    { cat: 'Beauty', pct: 67, color: '#C5A572' },
                  ]?.map((item, i) => (
                    <g key={i}>
                      <text x="16" y={204 + i * 14} fill="#ffffff60" fontSize="6" fontFamily="sans-serif">{item?.cat}</text>
                      <rect x="90" y={196 + i * 14} width="100" height="7" rx="3" fill="#ffffff10" />
                      <rect x="90" y={196 + i * 14} width={item?.pct} height="7" rx="3" fill={item?.color} opacity="0.7" />
                      <text x="196" y={204 + i * 14} fill={item?.color} fontSize="6" fontFamily="sans-serif">{item?.pct}%</text>
                    </g>
                  ))}

                  {/* Sales Velocity Bubbles */}
                  <rect x="216" y="178" width="196" height="94" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="224" y="190" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Sales Velocity</text>
                  {[
                    { cx: 260, cy: 225, r: 22, label: 'Electronics', color: '#C5A572' },
                    { cx: 310, cy: 215, r: 16, label: 'Apparel', color: '#60a5fa' },
                    { cx: 355, cy: 230, r: 12, label: 'Grocery', color: '#4ade80' },
                    { cx: 280, cy: 248, r: 8, label: 'Home', color: '#f472b6' },
                    { cx: 335, cy: 250, r: 10, label: 'Beauty', color: '#fbbf24' },
                  ]?.map((b, i) => (
                    <g key={i}>
                      <circle cx={b?.cx} cy={b?.cy} r={b?.r} fill={b?.color} opacity="0.25" stroke={b?.color} strokeWidth="0.5" strokeOpacity="0.5" />
                      <text x={b?.cx} y={b?.cy + 3} textAnchor="middle" fill={b?.color} fontSize="5.5" fontFamily="sans-serif">{b?.label}</text>
                    </g>
                  ))}
                </svg>
              </div>
              {/* Right: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between order-1 lg:order-2">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 2</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">Store Operations Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Real-time inventory visibility, stockout alerts, and sales velocity across all store locations.</p>
                  <div className="space-y-2 mb-8">
                    {['Inventory Stock Levels & Turnover', 'Stockout Rate & Fill Rate', 'Sales per Square Foot', 'Labor Efficiency']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard 3: Marketing / Merchandising */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left: Info */}
              <div className="p-8 md:p-10 flex flex-col justify-between">
                <div>
                  <div className="inline-block bg-navy/8 text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4">Dashboard 3</div>
                  <h3 className="font-serif text-navy text-2xl md:text-3xl font-bold mb-4">Marketing / Merchandising Dashboard</h3>
                  <p className="text-charcoal/70 text-sm mb-6 leading-relaxed">Customer segmentation, campaign ROI, and category performance to drive merchandising decisions.</p>
                  <div className="space-y-2 mb-8">
                    {['Customer Lifetime Value (CLV)', 'Campaign Effectiveness & ROAS', 'Promotional Impact on Margin', 'Category Mix Optimization']?.map((kpi, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0"></div>
                        <span className="text-charcoal text-sm">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button className="bg-navy hover:bg-navy/90 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all duration-300">View Demo</button>
                  <button className="bg-white hover:bg-gold/10 text-navy font-semibold px-5 py-2.5 rounded-lg border-2 border-navy text-sm transition-all duration-300">Request Customization</button>
                </div>
              </div>
              {/* Right: SVG Dashboard Mockup */}
              <div className="bg-[#0f1a2e] p-6 flex items-center justify-center min-h-[340px]">
                <svg viewBox="0 0 420 280" className="w-full max-w-[420px]" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="420" height="280" rx="8" fill="#1B2845" />
                  <rect x="0" y="0" width="420" height="32" rx="8" fill="#0f1a2e" />
                  <rect x="0" y="24" width="420" height="8" fill="#0f1a2e" />
                  <circle cx="14" cy="16" r="5" fill="#C5A572" opacity="0.6" />
                  <circle cx="28" cy="16" r="5" fill="#C5A572" opacity="0.3" />
                  <circle cx="42" cy="16" r="5" fill="#C5A572" opacity="0.15" />
                  <text x="200" y="20" textAnchor="middle" fill="#C5A572" fontSize="9" fontFamily="serif" opacity="0.8">Marketing / Merchandising Dashboard</text>

                  {/* RFM Grid */}
                  <rect x="8" y="40" width="190" height="110" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Customer RFM Segmentation</text>
                  {(() => {
                    const segments = [
                      ['Champions','Loyal','At Risk'],
                      ['Potential','Need Attn','Hibernating'],
                      ['New Cust.','Promising','Lost'],
                    ];
                    const colors = [
                      ['#4ade80','#C5A572','#fbbf24'],
                      ['#60a5fa','#f472b6','#f87171'],
                      ['#a78bfa','#34d399','#6b7280'],
                    ];
                    const sizes = [
                      [28, 22, 18],
                      [20, 16, 12],
                      [14, 18, 10],
                    ];
                    return segments?.map((row, ri) =>
                      row?.map((seg, ci) => (
                        <g key={`${ri}-${ci}`}>
                          <rect x={16 + ci * 58} y={58 + ri * 28} width="52" height="22" rx="3" fill={colors?.[ri]?.[ci]} opacity="0.2" stroke={colors?.[ri]?.[ci]} strokeWidth="0.5" strokeOpacity="0.4" />
                          <text x={42 + ci * 58} y={68 + ri * 28} textAnchor="middle" fill={colors?.[ri]?.[ci]} fontSize="5.5" fontFamily="sans-serif">{seg}</text>
                          <text x={42 + ci * 58} y={76 + ri * 28} textAnchor="middle" fill="#ffffff50" fontSize="5" fontFamily="sans-serif">{sizes?.[ri]?.[ci]}%</text>
                        </g>
                      ))
                    );
                  })()}

                  {/* Campaign ROI Table */}
                  <rect x="206" y="40" width="206" height="110" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="214" y="52" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Campaign ROI (ROAS)</text>
                  {/* Table header */}
                  <rect x="214" y="56" width="190" height="12" rx="2" fill="#C5A572" opacity="0.15" />
                  <text x="220" y="65" fill="#C5A572" fontSize="6" fontFamily="sans-serif">Campaign</text>
                  <text x="310" y="65" fill="#C5A572" fontSize="6" fontFamily="sans-serif">Spend</text>
                  <text x="360" y="65" fill="#C5A572" fontSize="6" fontFamily="sans-serif">ROAS</text>
                  {[
                    { name: 'Diwali Sale', spend: '₹12L', roas: '4.2×', color: '#4ade80' },
                    { name: 'Summer Promo', spend: '₹8L', roas: '3.1×', color: '#4ade80' },
                    { name: 'Loyalty Email', spend: '₹2L', roas: '6.8×', color: '#4ade80' },
                    { name: 'Flash Sale', spend: '₹5L', roas: '2.4×', color: '#fbbf24' },
                    { name: 'SMS Blast', spend: '₹1L', roas: '1.8×', color: '#f87171' },
                  ]?.map((row, i) => (
                    <g key={i}>
                      <text x="220" y={79 + i * 12} fill="#ffffff70" fontSize="6" fontFamily="sans-serif">{row?.name}</text>
                      <text x="310" y={79 + i * 12} fill="#ffffff50" fontSize="6" fontFamily="sans-serif">{row?.spend}</text>
                      <text x="360" y={79 + i * 12} fill={row?.color} fontSize="6.5" fontFamily="sans-serif" fontWeight="bold">{row?.roas}</text>
                    </g>
                  ))}

                  {/* Category Performance Matrix */}
                  <rect x="8" y="158" width="190" height="114" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="16" y="170" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Category Performance Matrix</text>
                  {[
                    { cat: 'Electronics', rev: 145, margin: 32, color: '#C5A572' },
                    { cat: 'Apparel', rev: 98, margin: 48, color: '#60a5fa' },
                    { cat: 'Grocery', rev: 210, margin: 18, color: '#4ade80' },
                    { cat: 'Home & Living', rev: 67, margin: 42, color: '#f472b6' },
                    { cat: 'Beauty', rev: 54, margin: 55, color: '#fbbf24' },
                  ]?.map((item, i) => (
                    <g key={i}>
                      <text x="16" y={184 + i * 16} fill="#ffffff60" fontSize="6" fontFamily="sans-serif">{item?.cat}</text>
                      <rect x="90" y={176 + i * 16} width={item?.rev * 0.45} height="7" rx="2" fill={item?.color} opacity="0.5" />
                      <text x={92 + item?.rev * 0.45} y={184 + i * 16} fill={item?.color} fontSize="5.5" fontFamily="sans-serif">₹{item?.rev}Cr</text>
                      <text x="170" y={184 + i * 16} fill="#ffffff40" fontSize="5.5" fontFamily="sans-serif">{item?.margin}%M</text>
                    </g>
                  ))}

                  {/* CLV Cards */}
                  <rect x="206" y="158" width="206" height="114" rx="4" fill="#ffffff05" stroke="#ffffff10" strokeWidth="0.5" />
                  <text x="214" y="170" fill="#ffffff80" fontSize="7" fontFamily="sans-serif">Customer Lifetime Value</text>
                  {[
                    { segment: 'Champions', clv: '₹48,200', growth: '+12%', color: '#4ade80' },
                    { segment: 'Loyal', clv: '₹28,500', growth: '+8%', color: '#C5A572' },
                    { segment: 'Potential', clv: '₹14,800', growth: '+22%', color: '#60a5fa' },
                    { segment: 'At Risk', clv: '₹9,200', growth: '-5%', color: '#f87171' },
                  ]?.map((card, i) => (
                    <g key={i}>
                      <rect x="214" y={176 + i * 22} width="190" height="18" rx="3" fill="#ffffff05" stroke={card?.color} strokeWidth="0.5" strokeOpacity="0.3" />
                      <text x="220" y={188 + i * 22} fill="#ffffff70" fontSize="6" fontFamily="sans-serif">{card?.segment}</text>
                      <text x="310" y={188 + i * 22} textAnchor="middle" fill={card?.color} fontSize="7" fontWeight="bold" fontFamily="sans-serif">{card?.clv}</text>
                      <text x="390" y={188 + i * 22} textAnchor="end" fill={card?.color} fontSize="6" fontFamily="sans-serif">{card?.growth}</text>
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}